import {
  types,
  Instance,
  SnapshotIn,
  getSnapshot,
  SnapshotOut
} from 'mobx-state-tree'
import { PageProps } from 'gatsby'
import { Project } from 'src/api/models/Project'
import { Font } from 'src/api/misc/Font'
import { Model } from 'src/api/misc/Model'
import { fontNames } from 'src/constants/font'
import { screw, nut, foot } from 'src/constants/models'
import { Color } from 'src/api/misc/Color'
import { default as colorNames } from 'src/constants/colors'
import { Editor } from 'src/api/stores/Editor'
import { Configuration } from 'src/api/stores/Configuration'
import { Preview } from 'src/api/stores/Preview'
import { toGlobalId, fromGlobalId, generateId } from 'src/api/utils/globalId'
import {
  project,
  editor,
  configuration,
  preview
} from 'src/constants/typenames'
import { orbit } from 'src/constants/sides'
import {
  projectConfigurationState,
  projectConfigurationSummaryState,
  projectEditorState,
  projectPreviewState,
  ProjectState,
  ConfigurationRouteState,
  EditorRouteState,
  PreviewRouteState,
  RouteState
} from 'src/constants/routes'
import { IPreview } from 'src/api/stores/Preview/types'
import { IProject } from 'src/api/models/Project/types'
import { IEditor } from 'src/api/stores/Editor/types'
import { IConfiguration } from 'src/api/stores/Configuration/types'
import { textileService } from 'src/api/textile'
import { commerceView } from 'src/api/stores/Root/commerce'

const FontMap = types.optional(
  types.map(Font),
  // fontNames.map(name => ({ name }))
  fontNames.reduce((p, name) => ({ ...p, [name]: { name } }), {})
)

const ModelMap = types.optional(
  types.map(Model),
  [screw, nut, foot].reduce((p, name) => ({ ...p, [name]: { name } }), {})
  // [
  //   ({ name: screw }, { name: nut }, { name: foot })
  // ]
)

const ColorMap = types.optional(
  types.map(Color),
  colorNames.reduce((p, name) => ({ ...p, [name]: { name } }), {})
)

// @ts-ignore
type ProjectInstance = Instance<IProject>
type PreviewInstance = Instance<IPreview>
type ConfigurationInstance = Instance<IConfiguration>
type EditorInstance = Instance<IEditor>

type Service = typeof textileService

const Views = types.union(types.late(Editor), Preview, Configuration)

// @ts-ignore
export const Root = types
  .model('Root', {
    views: types.map(Views),
    projects: types.map(types.late(Project)),
    fonts: FontMap,
    models: ModelMap,
    colors: ColorMap
  })
  .volatile((self) => ({
    service: undefined as Service | undefined
  }))
  .actions((self) => ({
    clear() {
      self.projects.clear()
      self.views.clear()
    },
    setProject(project: SnapshotIn<IProject>) {
      let projectData = project
      if (!project.id) {
        projectData = getSnapshot(Project().create(project))
      }
      self.projects.set(projectData.id as string, projectData)
    },
    clearProject(id: string) {
      self.projects.delete(id)
    },
    syncProject(projectSnapShot: SnapshotOut<IProject>) {
      self.service?.updateProject({
        ...projectSnapShot,
        updatedAt: new Date().valueOf()
      })
    },
    syncEditor(editorSnapShot: SnapshotOut<IEditor>) {
      // console.log('syncEditor')
      self.service?.saveEditor({
        ...editorSnapShot,
        updatedAt: new Date().valueOf()
      })
    },
    sync(
      projectSnapShot: SnapshotOut<IProject>,
      editorSnapShot: SnapshotOut<IEditor>
    ) {
      const updatedAt = new Date().valueOf()
      self.service?.updateProject({ ...projectSnapShot, updatedAt })
      self.service?.saveEditor({
        ...editorSnapShot,
        updatedAt: new Date().valueOf()
      })
    }
  }))
  .actions((self) => ({
    startSync(service: Service) {
      self.service = service
      self.projects.forEach(async (project) => {
        if (project.properties.name.value === 'Default') return
        const hasProject = await service.hasProject(project.id)
        if (!hasProject) {
          const data = getSnapshot(project)
          const response = await service.saveProject(data)
        } else {
          const isOwner = await service.isOwner(project.id)
          if (!isOwner) {
            self.clearProject(project.id)
          }
        }
      })
      service.getProjects((project: SnapshotIn<IProject>) =>
        self.setProject(project)
      )
    }
  }))
  .extend((self) => {
    let loading = true
    let promise: Promise<any>
    return {
      views: {
        getProject(id: Instance<ReturnType<typeof Project>>['id']) {
          return self.projects.get(id)
        },
        getEditor(ID: EditorInstance['id']): EditorInstance | undefined {
          const { type, id } = fromGlobalId(ID)
          if (type === editor) {
            return self.views.get(ID) as EditorInstance
          }
          return self.views.get(toGlobalId(editor, id)) as EditorInstance
        },
        getPreview(ID: string) {
          const { type, id } = fromGlobalId(ID)
          if (type === preview && self.views.has(ID)) {
            return self.views.get(ID)
          }
          return
        },
        get lastEdited(): EditorInstance | undefined {
          let lastEdited: EditorInstance | undefined
          self.views.forEach((value, key) => {
            if (
              fromGlobalId(key).type === editor &&
              value.createdAt.valueOf() > (lastEdited?.createdAt.valueOf() || 0)
            ) {
              lastEdited = value as EditorInstance
            }
          })
          return lastEdited
        },
        get loading() {
          if (loading) {
            throw promise
          } else {
            return loading
          }
        }
      },
      actions: {
        setLoaded(promisedLHydration: Promise<void>) {
          promise = promisedLHydration.then(() => {
            loading = false
          })
        },
        createProject() {
          const id = generateId()
          const projectId = toGlobalId(project, id)
          const configurationId = toGlobalId(configuration, id)
          self.projects.set(projectId, {
            id: projectId,
            properties: {
              name: { value: `new project ${self.projects.size + 1}` }
            }
          } as any)
          self.views.set(configurationId, {
            id: configurationId,
            draft: projectId,
            __typename: configuration
          } as SnapshotIn<typeof Configuration>)

          const projectInstance = self.projects.get(projectId)
          if (projectInstance) {
            const projectSnapshot = getSnapshot(projectInstance)
            self.service?.saveProject(projectSnapshot)
            const editorInstance = self.views.get(configurationId)
            if (editorInstance && editorInstance.__typename === 'Editor') {
              const editorSnapshot = getSnapshot(
                editorInstance as Instance<IEditor>
              )
              self.syncEditor(editorSnapshot)
            }
          }

          return self.views.get(configurationId)
        },
        editProject(projectID: ProjectInstance['id']): EditorInstance | null {
          const { id } = fromGlobalId(projectID)
          const editorId = toGlobalId(editor, id)
          if (self.views.has(editorId)) {
            return self.views.get(editorId) as EditorInstance
          } else if (self.projects.has(projectID)) {
            self.views.set(editorId, {
              id: editorId,
              draft: projectID,
              __typename: editor
            } as SnapshotIn<IEditor>)
            return self.views.get(editorId) as EditorInstance
          }
          return null
        },
        previewProject(
          projectID: ProjectInstance['id']
        ): PreviewInstance | null {
          const { type, id } = fromGlobalId(projectID)
          const previewId = toGlobalId(preview, id)
          if (self.views.has(previewId)) {
            return (self.views.get(previewId) as unknown) as PreviewInstance
          } else if (self.projects.has(projectID)) {
            self.views.set(previewId, {
              id: previewId,
              draft: projectID,
              __typename: preview
            })
            return (self.views.get(previewId) as unknown) as PreviewInstance
          }
          return null
        }
      }
    }
  })
  .actions((self) => ({
    getConfiguration(ID: string) {
      const { type, id } = fromGlobalId(ID)
      if (type === configuration && self.views.has(ID)) {
        return self.views.get(ID)
      } else {
        const configurationID = toGlobalId(configuration, id)
        if (self.views.has(configurationID)) {
          return self.views.get(configurationID)
        }
      }

      if (self.projects.has(toGlobalId(project, id))) {
        const editorId = toGlobalId(editor, id)
        if (self.views.has(editorId)) return self.views.get(editorId)
        const previewId = toGlobalId(preview, id)
        if (self.views.has(previewId)) return self.views.get(previewId)
        return self.previewProject(ID)
      }
      return null
    }
  }))
  .actions((self) => ({
    getStore({ state, projectID }: { state: ProjectState; projectID: string }) {
      switch (state) {
        case projectEditorState:
          return self.editProject(projectID)
        case projectConfigurationState:
        case projectConfigurationSummaryState:
          return self.getConfiguration(projectID)
        case projectPreviewState:
          return self.previewProject(projectID)
        default:
          return null
      }
    }
  }))
  // route State
  .volatile((self) => ({
    page: undefined as undefined | string,
    params: undefined as
      | undefined
      | (
          | ConfigurationRouteState
          | EditorRouteState
          | PreviewRouteState
        )['params'],
    state: undefined as
      | undefined
      | ((
          | ConfigurationRouteState
          | EditorRouteState
          | PreviewRouteState
        )['state'] &
          PageProps['location']['state']),
    view: undefined as any
  }))
  .views((self) => ({
    get routeState(): RouteState {
      return {
        page: self.page,
        store: self as any,
        ...(self.page === 'project' && {
          params: self.params,
          state: self.state,
          view: self.view
          // perceptions: self.view?.getPerceptions?.(self.state)
        })
      }
    }
  }))
  .actions((self) => ({
    setRouteState({
      location
    }: // '*': statePath,
    // projectID,
    // side
    SetRouteStateArg) {
      if (!location) return

      const [
        ,
        page = 'index',
        projectID,
        mode,
        step,
        subStep
      ] = location.pathname.split('/')

      let side = undefined
      if (mode === 'editor') side = !!step && step !== '' ? step : orbit

      // console.log('SET ROUTE STATE', side, statePath)
      // const page = location.pathname.split('/')[1] || 'index'
      self.page = page
      if (projectID) self.params = { projectID }
      const state: any = {}
      const stateParts = [mode, step, subStep].filter((v) => v !== undefined) //statePath?.split('/')
      state['name'] = stateParts
        ? stateParts[0]
        : projectID
        ? 'preview'
        : undefined
      if (stateParts && stateParts.length > 1) {
        if (stateParts[1] === 'summary') {
          state['name'] = 'summary'
        } else if (stateParts[0] === 'setup') {
          state['step'] = stateParts[1]
          state['subStep'] = stateParts[2]
        }
      }

      self.state = { ...state, ...location.state }
      if (page === 'project' && projectID) {
        const store = self.getStore({ state: self.state.name, projectID })

        if (side && store?.currentView?.side !== side) {
          store?.switchSide?.(side || orbit)
        }

        // if (store?.currentView?.side && store?.currentView?.side !== side) {
        //   //   store?.switchSide?.(side || 'orbit')
        // }
        self.view = store
      }
      return self.routeState
    }
  }))
  .actions((self) => ({
    afterCreate() {
      self.fonts.forEach((font) => font.load())
      self.models.forEach((model) => model.load())
      console.log('ROOT CREATED')
    }
  }))

interface SetRouteStateArg extends PageProps {
  '*'?: string
  projectID?: string
}

/*
   view: types.union(Editor, Preview, Configuration)
   */
